import React, { useEffect, useMemo, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { ErrorResponse } from '@/api/ErrorResponse'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormField,
  FormReadonlyField,
  FormSelectField,
  FormTextareaField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ClientDto } from 'api/Clients/Types/Client'
import { TradingAccountBalance, TradingAccountDto } from 'api/Clients/Types/TradingAccount'
import { TradingAccountToWalletTransfer } from 'api/Wallet/Types/TradingAccountToWalletTransfer'
import {
  CurrencyType,
  DragonPhoenixCurrencyType,
  PHPCurrencyType,
  WalletDto,
} from 'api/Wallet/Types/Wallet'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Link } from 'components/Link'
import { Loading } from 'components/Loading'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import toNumber from 'lodash/toNumber'
import { TriangleAlert } from 'lucide-react'
import { links } from 'utils/featureToggles'
import { isNotEmpty } from 'utils/is-empty'

export interface TradingAccountToWalletTransferFormValues {
  client: string | null
  fromTaId: string
  currencyBalance: TradingAccountBalance | null
  toWalletId: string
  amount: number
  comment: string
}

const TradingAccountToWalletTransferFormUI: React.FC<
  FormikProps<TradingAccountToWalletTransferFormValues> &
    OuterProps &
    TradingAccountToWalletTransferFormContainerProps
> = (props) => {
  const {
    walletTooltipTitle,
    tradingAccountTooltipTitle,
    selectedWallet,
    onSubmitForm,
    values,
    errors,
    setFieldValue,
    client,
    isSubmitting,
    hasWalletRestrictions,
    hasTradingAccountRestrictions,
    tradingAccounts,
  } = props

  useEffect(() => {
    props.resetForm()
  }, [client?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const currentTradingAccount = useMemo(
    () => tradingAccounts.find((item) => item.id === values.fromTaId),
    [tradingAccounts, values.fromTaId]
  )

  const tradingAccountWarning =
    hasTradingAccountRestrictions && tradingAccountTooltipTitle ? (
      <Tooltip>
        <TooltipTrigger>
          <TriangleAlert size={14} />
        </TooltipTrigger>
        <TooltipContent side='top'>{`Restrictions: ${tradingAccountTooltipTitle}`}</TooltipContent>
      </Tooltip>
    ) : undefined

  const walletWarning =
    hasWalletRestrictions && walletTooltipTitle ? (
      <Tooltip>
        <TooltipTrigger>
          <TriangleAlert size={14} />
        </TooltipTrigger>
        <TooltipContent side='top'>{`Restrictions: ${walletTooltipTitle}`}</TooltipContent>
      </Tooltip>
    ) : undefined

  const tradingAccountRightAddon =
    values.fromTaId || tradingAccountWarning ? (
      <>
        {values.fromTaId && (
          <Link
            to={generatePath(links.recordsTradingAccounts.detailsUrl as string, {
              id: values.fromTaId,
              slug: 'details',
            })}
            showIcon
          />
        )}
        {tradingAccountWarning}
      </>
    ) : undefined

  const walletRightAddon =
    selectedWallet?.id || walletWarning ? (
      <>
        {selectedWallet?.id && (
          <Link
            to={generatePath(links.recordsWallets.detailsUrl as string, {
              id: selectedWallet.id,
              slug: 'details',
            })}
            showIcon
          />
        )}
        {walletWarning}
      </>
    ) : undefined

  const walletValue = selectedWallet
    ? `${selectedWallet.name}-${selectedWallet.balance}`
    : values.currencyBalance?.currency
      ? `Wallet does not exists for currency ${values.currencyBalance.currency}`
      : 'Select currency...'

  return (
    <Loading isLoading={props.isLoading}>
      <Form onSubmit={onSubmitForm} data-test='transaction-form'>
        <DataGrid columns={1}>
          <DataColumn>
            <DataCard title='Transfer' columns={2}>
              <FormReadonlyField
                name='client'
                label='Client'
                value={values.client ?? ''}
                dataTest='client-field'
                error={errors.client}
                rightAddon={
                  <>
                    {props.setClient && (
                      <Button type='button' size='sm' variant='outline' onClick={props.setClient}>
                        Select
                      </Button>
                    )}
                    {client?.id && (
                      <Link
                        to={generatePath(links.recordsClients.detailsUrl as string, {
                          id: client.id,
                          slug: 'details',
                        })}
                        showIcon
                      />
                    )}
                  </>
                }
              />
              <FormSelectField
                name='fromTaId'
                label='Trading Account'
                value={values.fromTaId || undefined}
                options={tradingAccounts.map((ta) => ({
                  id: ta.id,
                  name: `${ta.name}-${ta.accountGroup.name}`,
                }))}
                error={errors.fromTaId}
                showSearch
                onChange={(value) => {
                  const nextTradingAccountId = value ? String(value) : ''
                  setFieldValue('fromTaId', nextTradingAccountId)
                  setFieldValue('currencyBalance', null)
                  setFieldValue('toWalletId', '')
                }}
                rightAddon={tradingAccountRightAddon}
              />
              <FormSelectField
                name='currencyBalance'
                label='Currency Balance'
                value={values.currencyBalance?.currency}
                options={
                  currentTradingAccount?.platformOverview.balances
                    .filter((balance) => balance.balance > 0)
                    .map((balance) => ({
                      id: balance.currency,
                      name: `${balance.currency}, balance: ${balance.balance}`,
                    })) ?? []
                }
                error={errors.currencyBalance}
                onChange={(value) => {
                  if (!currentTradingAccount) {
                    setFieldValue('currencyBalance', null)
                    setFieldValue('toWalletId', '')
                    return
                  }

                  const nextBalance =
                    currentTradingAccount.platformOverview.balances.find(
                      (balance) => balance.currency === value
                    ) ?? null

                  setFieldValue('currencyBalance', nextBalance)

                  if (nextBalance) {
                    props.onSelectWalletForTradingAccount(currentTradingAccount.id, nextBalance)
                  } else {
                    setFieldValue('toWalletId', '')
                  }
                }}
                dataTest='trading-account-currency-field'
              />
              <FormReadonlyField
                name='toWalletId'
                label='Wallet'
                value={walletValue}
                dataTest='wallet-field'
                error={errors.toWalletId}
                rightAddon={walletRightAddon}
              />
              <FormField
                name='amount'
                label='Amount'
                type='number'
                error={errors.amount}
                dataTest='amount-field'
                rightAddon={
                  props.currency ? (
                    <span className='inline-flex items-center px-3 text-sm text-muted-foreground'>
                      {props.currency}
                    </span>
                  ) : undefined
                }
              />
              <FormTextareaField
                name='comment'
                label='Comment'
                rows={3}
                maxLength={130}
                dataTest='comment-field'
              />
            </DataCard>
          </DataColumn>
        </DataGrid>
        <div className='mt-4 flex justify-center'>
          <Button disabled={isSubmitting} data-test='submit' type='submit'>
            Submit
          </Button>
        </div>
      </Form>
    </Loading>
  )
}

interface TradingAccountToWalletTransferFormContainerProps {
  onSubmitForm: (event: React.FormEvent<HTMLFormElement>) => void
}

const TradingAccountToWalletETDTransferFormContainer: React.FC<
  FormikProps<TradingAccountToWalletTransferFormValues> & OuterProps
> = (props) => {
  const { overrideWarningError } = props
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false)
  const [isRestrictionModalOpen, setRestrictionModalOpen] = useState(false)

  const handleSubmitForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const nextErrors = await props.validateForm(props.values)

    if (Object.keys(nextErrors).length > 0) {
      return
    }

    if (props.hasWalletRestrictions || props.hasTradingAccountRestrictions) {
      setRestrictionModalOpen(true)
      return
    }

    setConfirmationModalOpen(true)
  }

  const handleRestrictionSubmit = () => {
    setConfirmationModalOpen(true)
    setRestrictionModalOpen(false)
  }

  const handleConfirmationSubmit = async () => {
    try {
      await props.submitForm()
      setConfirmationModalOpen(false)
      props.resetForm()
      props.resetAll()
    } catch (error) {
      console.error('Cannot create transaction', error)
      setConfirmationModalOpen(false)
      throw error
    }
  }

  return (
    <>
      {isConfirmationModalOpen && (
        <ConfirmationModal
          yesButtonText='Proceed'
          onYes={handleConfirmationSubmit}
          onCancel={() => setConfirmationModalOpen(false)}
          visible={isConfirmationModalOpen}
          renderTitle={
            <div>
              <p>
                You are going to transfer fund from the trading account{' '}
                {props.selectedTradingAccount?.name} to the wallet {props.selectedWallet?.name}. The
                parameters selected are:
              </p>
              <div>
                <div id='field'>
                  <strong>Client:</strong> {props.client?.name}
                </div>
                <div id='field'>
                  <strong>From Trading Account:</strong> {props.selectedTradingAccount?.name}
                </div>
                <div id='field'>
                  <strong>To Wallet:</strong> {props.selectedWallet?.name}
                </div>
                <div id='field'>
                  <strong>Debit Amount:</strong> {props.values.amount}{' '}
                  {props.selectedWallet?.currency.id}
                </div>
              </div>
            </div>
          }
        />
      )}
      {isRestrictionModalOpen && (
        <ConfirmationModal
          yesButtonText='Yes, proceed'
          onYes={handleRestrictionSubmit}
          onCancel={() => setRestrictionModalOpen(false)}
          visible={isRestrictionModalOpen}
          renderTitle={
            <div>There is a restriction in place which agent can acknowledge before proceeding</div>
          }
        />
      )}
      {!!overrideWarningError && (
        <ConfirmationModal
          yesButtonText='Proceed'
          onYes={handleConfirmationSubmit}
          onCancel={() => {
            props.resetForm()
            props.resetAll()
          }}
          visible={!!overrideWarningError}
          renderTitle={<p>{overrideWarningError?.message}</p>}
        />
      )}
      <TradingAccountToWalletTransferFormUI {...props} onSubmitForm={handleSubmitForm} />
    </>
  )
}

interface OuterProps {
  isLoading: boolean
  currency: null | CurrencyType | DragonPhoenixCurrencyType | PHPCurrencyType | string
  client: ClientDto | null
  close?(): void
  setClient?(): void
  tradingAccounts: TradingAccountDto[]
  selectedWallet: WalletDto | undefined
  selectedBalance: TradingAccountBalance | undefined
  selectedTradingAccount: TradingAccountDto | undefined
  overrideWarningError: ErrorResponse | undefined
  onSelectWalletForTradingAccount(tradingAccountId: string, taBalance: TradingAccountBalance): void
  onSubmit(values: TradingAccountToWalletTransfer): void
  resetAll(): void
  tradingAccountTooltipTitle?: string
  walletTooltipTitle?: string
  hasWalletRestrictions: boolean
  hasTradingAccountRestrictions: boolean
}

const dataToSubmit = (
  values: TradingAccountToWalletTransferFormValues & { selectedWallet: WalletDto }
): TradingAccountToWalletTransfer => ({
  fromTaId: values.fromTaId,
  toWalletId: values.selectedWallet.id,
  amount: toNumber(values.amount),
  comment: isNotEmpty(values.comment) ? values.comment : null,
  overrideFreeMarginCheck: false,
})

export default withFormik<OuterProps, TradingAccountToWalletTransferFormValues>({
  mapPropsToValues: ({ client, selectedTradingAccount, selectedWallet, selectedBalance }) => ({
    client: client?.name || '',
    fromTaId: selectedTradingAccount ? selectedTradingAccount.id : '',
    currencyBalance: selectedBalance ?? null,
    toWalletId: selectedWallet?.id || '',
    amount: 0,
    comment: '',
  }),
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      if (values.fromTaId && props.selectedWallet) {
        await props.onSubmit(
          dataToSubmit({
            ...values,
            selectedWallet: props.selectedWallet,
          })
        )
      }
    } catch (error) {
      console.error('Cannot create transaction', error)
      throw error
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values, { tradingAccounts }) => {
    const errors: FormikErrors<TradingAccountToWalletTransferFormValues> = {}
    const selectedTradingAccount = tradingAccounts.find((item) => item.id === values.fromTaId)

    if (!values.fromTaId) {
      errors.fromTaId = 'Required'
    }

    if (!values.currencyBalance) {
      errors.currencyBalance = 'Required'
    } else if (values.currencyBalance.balance <= 0) {
      errors.currencyBalance = 'Must be greater than 0'
    }

    if (!values.toWalletId) {
      errors.toWalletId = 'Required'
    }

    if (!values.amount) {
      errors.amount = 'Required'
    } else if (toNumber(values.amount) <= 0) {
      errors.amount = 'Must be greater than 0'
    }

    if (selectedTradingAccount && toNumber(selectedTradingAccount.platformOverview.equity) <= 0) {
      errors.fromTaId = 'Must be greater than 0'
    }

    if (
      selectedTradingAccount &&
      values.currencyBalance &&
      toNumber(values.amount) > 0 &&
      values.currencyBalance.balance < values.amount
    ) {
      errors.amount = 'Amount exceed available balance'
    }

    return errors
  },
  enableReinitialize: true,
  isInitialValid: false,
})(TradingAccountToWalletETDTransferFormContainer)
